/**
 * Invoice-surface copy, base (legal) dialect. The en catalog is complete by
 * definition - its object type IS the key schema every other locale overrides.
 * Store natural-cased strings; presentation casing (e.g. the PDF's uppercase
 * section headings) is applied at the render site.
 */
export const invoice = {
    totals: {
        grossSubtotal: 'Subtotal before discount',
        /** Replaces grossSubtotal when the user has renamed the discount row (e.g. "Less AFSA levy"). */
        grossSubtotalBeforeAdjustment: 'Subtotal before adjustment',
        subtotalExcludingVat: 'Subtotal excluding VAT',
        discount: 'Discount',
        total: 'Total',
        feesVat: 'VAT on fees',
        disbursementsVat: 'VAT on disbursements',
        feesAfterDiscount: 'Fees after discount',
        feesAfterAdjustment: 'Fees after adjustment',
        disbursementsAfterDiscount: 'Disbursements after discount',
        disbursementsAfterAdjustment: 'Disbursements after adjustment',
    },
    number: {
        /** Shown in place of the invoice number on a draft that has none stored (editor Number card and PDF). */
        notYetNumbered: 'Not yet numbered',
    },
    pdf: {
        /** Meta label above the client-project reference. */
        projectReference: 'Matter',
        /** Fee-side section heading (render suppressed when practice config showFeesHeading is false). */
        feesSection: 'Fees',
        /** Disbursement-side section heading. */
        disbursementsSection: 'Disbursements',
        /** Fee-side subtotal row label (mixed invoices). */
        feesSubtotal: 'Fees subtotal',
        /** Disbursement-side subtotal row label (mixed invoices). */
        disbursementsSubtotal: 'Disbursements subtotal',
    },
    editor: {
        /** Fee-side option on the line-type toggle. */
        feeLineType: 'Fee',
        /** Disbursement-side option on the line-type toggle. */
        disbursementLineType: 'Disbursement',
        /** Link under the locked amount and type controls of a line added from a recorded expense; opens that expense. */
        fromExpenseAmountLocked: 'The amount comes from the recorded expense',
        /** Project metadata-card label, shown in both the closed and open (editing) states. */
        projectCardLabel: 'Matter',
        /** Empty state shown in the closed project cell when no subject line is set. */
        noProjectName: 'Add a matter name',
        /** Placeholder for the project-name input inside MatterEditPopover. */
        projectNamePlaceholder: 'Add matter name',
        /** Accessible label for the clear (X) button inside MatterEditPopover. */
        clearProjectAriaLabel: 'Clear matter',
        /** Hint under the project-name input in the portal (non-inline) popover variant. */
        projectRowHint: 'Leave blank to hide the matter row on the invoice PDF.',
        /** Inline error fallback when the project-name mutation fails without a server message. */
        projectUpdateError: 'Could not update matter name.',
        /** Zod validation message: project-name input over the character limit. */
        projectNameTooLong: 'Matter name must be 200 characters or less',
        /** Plain field label (no trailing "*") for the matter/project name - used where the field is optional, e.g. CorrectInvoiceDialog's subject-line input and its correction-history label. */
        projectFieldLabel: 'Matter name',
        /** Fee-side section heading in the draft editor's line-item table (distinct from the PDF's pdf.feesSection - this is the on-screen editor, not the rendered document). */
        feesSectionHeading: 'Fees',
        /** Disbursement-side section heading in the draft editor's line-item table. */
        disbursementsSectionHeading: 'Disbursements',
        /** Value of the "To" card on a draft that has no client yet. */
        chooseClient: 'Choose a client',
        /** Matter card (card 3) and the matter step of the "Choose client and matter" dialog. */
        matter: {
            /** Label of the Matter card while its inline editor changes the name printed on the invoice. */
            headingLabel: 'Name on invoice',
            /** Card value: no matter, and every line is a direct or discount line. */
            noMatterDirectOnly: 'No matter – direct charges only',
            /** Card value: no matter, but the draft holds activity or expense lines. */
            noMatter: 'No matter',
            /** Card value on a draft with no client; also the title of the dialog's client step. */
            chooseClientAndMatter: 'Choose client and matter',
            /** Omnibox prompt on a draft that has a client but no matter; opens the dialog on the matter step. */
            chooseMatter: 'Choose a matter',
            /** Badge beside the name of an archived matter. */
            archivedBadge: 'Archived',
            /** Card value when the matter's name could not be read. */
            nameUnavailable: "Couldn't load the matter name",
            /** Action label on the card beside the matter name; opens the menu of the two actions below. */
            changeAction: 'Change',
            /** Accessible name of the Change action, which the visible word alone does not give. */
            changeAriaLabel: 'Change matter or name on invoice',
            /** Menu item: edit only the name printed on this invoice. */
            renameAction: 'Rename on this invoice',
            /** Menu item: open the dialog to move the invoice to another matter. */
            switchAction: 'Switch matter',
            /** Muted second line of the card when the name on the invoice differs from the matter's own name. */
            matterLine: 'Matter: {{name}}',
            /** Matter step: dialog title. */
            stepTitle: 'New matter for this invoice',
            /** Matter step: accessible name of the search field. */
            searchAriaLabel: 'Search matters',
            searchPlaceholder: 'Search matters...',
            /** Matter step: accessible name of the list. */
            listAriaLabel: 'Matters',
            /** Matter step: tag on the matter the invoice already has. */
            currentLabel: 'Current',
            /** Matter step: sublabel on the client's matter that has the same name as the work's current matter. */
            sameNameHint: 'Same name as the current matter',
            /** Matter step: the search failed. */
            loadError: "Couldn't load matters. Try searching again.",
            /** Matter step: the typed search matches nothing. */
            noSearchMatches: 'No matters match “{{query}}”.',
            /** Matter step: nothing is left after filtering to the invoice's currency. */
            noMattersInCurrency: '{{client}} has no matters billed in {{currency}}.',
            /** Matter step: the loaded matters are all in another currency, but more can be loaded. */
            noMattersInCurrencyYet: 'None of the matters loaded so far are billed in {{currency}}. Load more to keep looking.',
            /** Matter step footer: opens the new-matter form with no name typed. */
            createAction: 'Create a new matter',
            /** Matter step footer: opens the new-matter form with the searched name. */
            createNamedAction: 'Create “{{name}}”',
            /** New-matter form: field label. */
            newNameLabel: 'Matter name',
            /** New-matter form: empty name. */
            newNameRequired: 'Enter a matter name',
            /** New-matter form: submit. */
            createAndUse: 'Create and use matter',
            /** Footer action returning to the previous step. */
            back: 'Back',
            /** Matter step notice on a client switch with work; plural on {{count}}. */
            moveNote_one: 'The {{count}} entry on this invoice moves to the matter you choose for {{client}}.',
            moveNote_other: 'The {{count}} entries on this invoice move to the matter you choose for {{client}}.',
            /** Confirm view of a client switch with work, once the matter is chosen; plural on {{count}}. */
            confirmMoveNote_one: 'The {{count}} entry on this invoice moves to {{matter}}.',
            confirmMoveNote_other: 'The {{count}} entries on this invoice move to {{matter}}.',
            /** Confirmation of a change of matter on a draft with work; plural on {{count}}, {{matters}} names where it comes from. */
            removeNote_one: 'The {{count}} entry from {{matters}} will be removed from this invoice and go back to unbilled.',
            removeNote_other: 'The {{count}} entries from {{matters}} will be removed from this invoice and go back to unbilled.',
            /** Confirm button of the work-carrying change. */
            confirmRemove: 'Remove and change matter',
            confirmMove: 'Move entries',
            /** Dialog title of the confirmation view. */
            confirmTitle: 'Change matter',
            /** Retry label after a failed attempt, which resends the same request. */
            retry: 'Retry',
            /** Explains why the choice is locked after a failed attempt. */
            retryNote: 'Retry sends the same change. To choose again, go Back.',
            /** Fallback when the change fails without a usable message. */
            changeFailed: "Couldn't change the matter. Try again.",
            /** Fallback when setting the matter without work fails. */
            setFailed: "Couldn't set the matter. Try again.",
            /** Accessible name of the placeholder shown while the matter's name loads on the Matter card. */
            loadingLabel: 'Loading',
            /** Choose client and matter dialog: shown while the invoice's attached work is read. */
            checkingWork: "Checking the invoice's work...",
            /** Choose client and matter dialog: the attached-work read failed. */
            workLoadError: "Couldn't load the invoice's work.",
            /** Choose client and matter dialog: retry button after the attached-work read failed. */
            workLoadRetry: 'Try again',
            /** Choose client and matter dialog: stands in for a client whose name is not known yet. */
            thisClient: 'This client',
            /** Client step of the dialog: accessible name of the search field. */
            clientSearchAriaLabel: 'Search clients',
            /** Client step: accessible name of the client list. */
            clientListAriaLabel: 'Clients',
            clientSearchPlaceholder: 'Search clients...',
            /** Client step: marks the invoice's current client in the list. */
            currentClientLabel: 'Current',
            clientsLoadError: "Couldn't load clients. Try searching again.",
            /** Client step: the search matched no client; interpolates {{query}}. */
            noClientsMatch: 'No clients match “{{query}}”.',
            noClients: 'No clients.',
            /** Client step footer: starts a new client named for the search text; interpolates {{query}}. */
            createClientNamed: 'Create “{{query}}”',
            createClientAction: 'Create a new client',
        },
        /** Note in the client picker when switching the draft's client will clear its matter name and reference. */
        switchClientClearsProject: "Changing client clears the invoice’s matter name and reference.",
        /** Confirmation before saving the firm's own details from the From panel of a draft. */
        fromDetailsConfirm: {
            title: 'Change your practice details',
            message: "This changes your own practice details, not your client's. Your drafts and every invoice you approve from now on will use them, and invoices already issued keep their current details. Save the changes?",
            confirmLabel: 'Save practice details',
            cancelLabel: 'Go back',
        },
        /** Footer link in the From form to the canonical practice-details settings. Opens in a new tab. */
        fromDetailsSettingsLink: {
            label: 'Edit in Settings',
            /** Screen-reader-only suffix announcing the new tab. */
            newTabHint: '(opens in a new tab)',
        },
        /** Tooltip on the disabled Approve button. */
        approveDisabled: {
            /** The draft has no client. */
            noClient: 'Choose a client before approving',
            /** The draft has a client but no lines. */
            noLines: 'Add a line item before approving',
        },
        /** The "Add line item" omnibox on the draft editor. */
        omnibox: {
            /** Hint line under the closed ghost pill's "Add line item" label. */
            closedHint: 'Start typing to search activities or disbursements, or to create a new line',
            /** Placeholder for the open search input. */
            searchPlaceholder: 'Search activities or disbursements, or type to create a line…',
            /** Dropdown empty state before the user has typed anything. */
            emptyHint: 'Start typing to search unbilled activities or approved disbursements.',
            /** Dropdown empty state when the query matches nothing. */
            noMatches: 'No matching activities or disbursements found.',
            /** Closed-pill hint on a draft with no client, where only direct charges can be added. */
            clientlessClosedHint: 'Type a description to add a direct charge',
            /** Placeholder for the open search input on a draft with no client. */
            clientlessSearchPlaceholder: 'Describe a direct charge...',
            /** Dropdown empty state before typing, on a draft with no client. */
            clientlessEmptyHint: 'Type a description to add a direct charge. Choose a client to add recorded work or disbursements.',
            /** Dropdown empty state when the query matches nothing, on a draft with no client. */
            clientlessNoMatches: 'Choose a client to add recorded work or disbursements.',
            /** Accessible label for the expense-lane group in the results dropdown (visible sibling label is DisbursementBadge). */
            expenseLaneAriaLabel: 'Approved disbursements',
            /** Badge on an activity result already sitting on another draft's live pro forma - naming it warns the user before they pick a row the move would then refuse. */
            frozenResultBadge: 'On {{proformaNumber}}',
            /** Note under the results: other matters of the client match the typed term; plural on {{count}}. */
            otherMatterMatches_one: '{{count}} more match on {{matter}}. An invoice covers one matter.',
            otherMatterMatches_other: '{{count}} more matches on {{matter}}. An invoice covers one matter.',
            /** Option row in that note: opens the new-invoice picker with the matter chosen. */
            newInvoiceFor: 'New invoice for {{matter}}',
            /** Dropdown hint on a draft with a client but no matter, before typing. */
            noMatterEmptyHint: 'Type a description to add a direct charge. Choose a matter to add activities and disbursements.',
            /** Dropdown hint on a draft with a client but no matter, when the typed term matches nothing. */
            noMatterNoMatches: 'Choose a matter to add activities and disbursements.',
        },
        /** Toast shown when adding a from-expense line item 409s because the expense is already billed elsewhere (double-bill guard or concurrent add). */
        expenseAlreadyBilled: {
            /** Toast title. */
            title: 'Disbursement already billed',
            /** Toast description. */
            description: 'This disbursement is already on a line item on this or another invoice.',
        },
        /** Pro forma issuing lifecycle on the draft editor: the pro forma stack, its send/mark-sent/cancel actions, and the composition-disabled reason. Catalog keys keep "withdraw" because that is the backend action; the user-facing verb is cancel. */
        proforma: {
            /** Accessible name for the pro forma stack under the draft editor's header. */
            stackLabel: 'Pro formas',
            /** A strip's heading, naming the pro forma. */
            stripTitle: 'Pro forma {{proformaNumber}}',
            /** Format label beside a stored PDF pro forma. */
            formatPdf: 'PDF',
            /** Format label beside a stored Word pro forma. */
            formatWord: 'Word',
            /** State of a live pro forma: sent (by email or marked as sent) and not cancelled. */
            stateSent: 'sent {{date}}',
            /** State of a pro forma that was generated but never sent. */
            stateGenerated: 'generated {{date}}, not sent',
            /** State of a cancelled pro forma (backend: withdrawn). */
            stateWithdrawn: 'cancelled {{date}}',
            /** Appended to the live strip when the invoice's money figures no longer match what was sent. */
            divergenceNotice: 'figures have changed since sending',
            /** Appended to an unsent strip when the invoice no longer matches this pro forma. */
            outOfDateNotice: 'out of date',
            /** Line under the live strip, saying what the live pro forma blocks. Only the line-item set is frozen; a line's figures can still be edited (the strip then flags the difference), so this must not claim the lines themselves are locked. */
            liveLockNote: 'Lines can\'t be added, removed or moved until it is cancelled.',
            /** Heading of the collapsed list of older pro formas. */
            historyTitle: 'Earlier pro formas',
            /** Why Send is unavailable on a Word pro forma: a client is only ever emailed a PDF. */
            sendBlockedWord: 'Word pro formas are not emailed from Bill. Generate a PDF pro forma to send.',
            /** Why Send is unavailable when the pro forma has no stored document. */
            sendBlockedNoDocument: 'This pro forma has no stored document. Generate a new one to send.',
            /** Why Send is unavailable when the invoice changed (figures or lines) after the pro forma was made. The strip already flags "out of date". */
            sendBlockedChanged: 'Generate a new pro forma to send.',
            /** Why both Send and Mark pro forma as sent are unavailable when the invoice's lines changed. The strip already flags "out of date". */
            blockedByLines: 'Generate a new pro forma to send or mark as sent.',
            /** Menu item generating (and downloading) a new PDF pro forma. */
            generatePdfAction: 'Generate PDF pro forma',
            /** Menu item generating (and downloading) a new Word pro forma, offered only for firms with a custom Word format. */
            generateWordAction: 'Generate Word pro forma',
            /** Success toast after emailing a pro forma. */
            sendSuccess: 'Pro forma {{proformaNumber}} sent',
            /** Success toast after marking a pro forma as sent. */
            markSentSuccess: 'Pro forma {{proformaNumber}} marked as sent',
            /** Success toast after cancelling a pro forma. */
            withdrawSuccess: 'Pro forma {{proformaNumber}} cancelled',
            /** Reason shown on a disabled composition control (the add-line omnibox) while a pro forma is live. */
            compositionDisabledReason: 'Pro forma {{proformaNumber}} was sent on {{sentDate}}. Cancel it to add, remove or move lines.',
            /** Reason shown on the Generate PDF / Word menu items while the draft has no client. */
            generateNoClientReason: 'Choose a client before generating a pro forma.',
            /** Reason shown on the Generate PDF / Word menu items while a pro forma is live. */
            generateDisabledReason: 'Pro forma {{proformaNumber}} was sent on {{sentDate}}. Cancel it to generate a new pro forma.',
            /** "Send pro forma" action label on a strip, opening SendInvoiceDialog in proforma mode for that pro forma. */
            sendAction: 'Send pro forma',
            /** "Mark pro forma as sent" action label, attesting an already-generated pro forma was delivered outside Bill. */
            markSentAction: 'Mark pro forma as sent',
            /** Cancel-pro-forma action label - a danger action, unfreezing the draft. */
            withdrawAction: 'Cancel pro forma',
            /** Cancel confirm dialog title. */
            withdrawConfirmTitle: 'Cancel pro forma',
            /** Cancel confirm dialog body, naming the pro forma. */
            withdrawConfirmBody: 'Cancel {{proformaNumber}}? You\'ll be able to add, remove or move lines again.',
            /** Cancel confirm button label. */
            withdrawConfirmButton: 'Cancel pro forma',
            /** Mark-as-sent confirm dialog title. */
            markSentConfirmTitle: 'Mark pro forma as sent',
            /** Mark-as-sent confirm dialog body, naming the pro forma. */
            markSentConfirmBody: 'Record that {{proformaNumber}} was delivered outside Bill? This invoice will freeze until it is cancelled.',
            /** Mark-as-sent confirm button label. */
            markSentConfirmButton: 'Mark pro forma as sent',
            /** Appended to the delete-draft confirm body when the draft carries a live pro forma, naming it and its sent date. */
            deleteWithLiveProformaNotice: 'Pro forma {{proformaNumber}} was sent on {{sentDate}} and will no longer be valid.',
            /** Appended to the Approve confirm body when the invoice no longer matches its live pro forma's snapshot. */
            approveDivergenceNotice: 'This invoice no longer matches pro forma {{proformaNumber}}, sent to the client on {{sentDate}}.',
            /** Send-history row label for a pro forma send, in place of the ordinary invoice-send label. */
            sendHistoryLabel: 'Pro forma {{proformaNumber}}',
            /** Move-conflict dialog title when the source draft is frozen by a live pro forma - the cancel-first variant of the ordinary "Activity already on another invoice" dialog. */
            moveConflictLockedTitle: 'Activity on a sent pro forma',
            /** Move-conflict dialog body naming the pro forma and its invoice; no "Move here" is offered in this state. */
            moveConflictLockedBody: 'This activity is on {{proformaNumber}} ({{invoiceNumber}}), which was sent. Cancel it first to move this activity.',
            /** Reason shown beside a batch-move row whose source invoice is frozen - it stays listed but cannot be moved with the rest. */
            lockedMoveReason: '{{proformaNumber}} was sent - cancel it first',
            /** Download action on a pro forma strip, history row, or send-history row - re-opens the exact stored file, in place of regenerating (which would mint a new PF number). */
            downloadAction: 'Download',
            /** Accessible name for the send-history row's download button, naming the pro forma alongside the visible "Download" text. */
            downloadAriaLabel: 'Download {{proformaNumber}}',
            /** Toast shown when the stored pro forma document fails to download. */
            downloadError: 'Could not download the pro forma document. Try again.',
        },
    },
    /** Notices that come back with a successful approval, for invoices that predate the one-matter rule. */
    approveWarning: {
        /** The invoice has no matter but holds matter work. */
        clientLevel: {
            title: 'Approved at client level',
            description: "It has no matter, so it will show on the client's statement but on no matter's statement.",
        },
        /** The invoice's matter is archived; interpolates {{matter}}. */
        matterArchived: {
            title: 'Matter is archived',
            description: '{{matter}} is archived. The invoice was still approved for it.',
        },
        /** The issued invoice also holds work from other matters; interpolates {{matter}} (the invoice's) and {{names}}. */
        otherMatterWork: {
            title: 'Work from other matters',
            description: 'This invoice is for {{matter}}, but it also holds work from {{names}}.',
        },
        /** The draft already bills expenses that have no matter; plural on {{count}}. */
        overheadExpense: {
            title: 'Expense with no matter billed',
            description_one: 'This invoice bills {{count}} expense with no matter. Expenses with no matter are firm overhead and can no longer be added to invoices.',
            description_other: 'This invoice bills {{count}} expenses with no matter. Expenses with no matter are firm overhead and can no longer be added to invoices.',
        },
    },
    /** Invoice-send email, read by the account's own client (an external non-user). */
    email: {
        /**
         * Greeting sentence when the invoice carries a client-project reference.
         * The render site wraps the interpolated values in `<strong>` markup;
         * the catalog string itself stays presentation-free.
         */
        attachedGreetingWithReference: 'Invoice {{invoiceNumber}} for matter {{reference}} is attached.',
        /** Greeting sentence when the invoice has no client-project reference. */
        attachedGreeting: 'Invoice {{invoiceNumber}} is attached.',
        /** Pro forma equivalent of attachedGreetingWithReference - sendProforma's email. */
        attachedGreetingWithReferenceProforma: 'Pro forma invoice {{invoiceNumber}} for matter {{reference}} is attached.',
        /** Pro forma equivalent of attachedGreeting - sendProforma's email. */
        attachedGreetingProforma: 'Pro forma invoice {{invoiceNumber}} is attached.',
    },
    /** New-invoice flow: the client + client-project picker, pre-line-editor. */
    new: {
        pageTitle: 'New invoice',
        searchClientsAriaLabel: 'Search clients',
        searchClientsPlaceholder: 'Search all clients...',
        loading: 'Loading...',
        searching: 'Searching...',
        /** The client search matched nothing. */
        noClientsMatchSearch: 'No clients match your search',
        cancel: 'Cancel',
        continue: 'Continue',
        /** Row detail after a separator; interpolates {{date}}. */
        lastActivity: 'Last activity {{date}}',
        /** Client row with nothing to bill. */
        noUnbilledActivities: 'No unbilled activities',
        /** Matter row: the amount still to bill; interpolates {{amount}}. */
        outstandingAmount: '{{amount}} outstanding',
        /** Right-pane empty state before a client is picked. */
        selectClientHelper: 'Select a client to choose matters',
        /**
         * Right-pane empty state before a client is picked, when nothing at all
         * is ready to bill - there are no matters to choose, so the helper names
         * the step that is actually available.
         */
        selectClientNoWorkHelper: 'Select a client to create an invoice',
        /** Empty state when the account has no unbilled work and no clients. */
        noClients: 'No clients',
        /** Link out of the no-clients empty state, to the client form. */
        addClient: 'Add a client',
        /** Error alert when the client list fails to load. */
        loadClientsError: "Couldn't load clients",
        /**
         * Shown under the client list when the account has more clients than the
         * one page fetched, so a partial list is never read as the whole set.
         */
        clientListTruncatedHint: 'Not all clients are shown. Search to find others.',
        /** Error alert when the ready-to-bill client-project list fails to load. */
        loadProjectsError: "Couldn't load matters with unbilled work",
        /** Client-row project count; i18next plural pair, interpolates {{count}}. */
        projectCount_one: '{{count}} matter',
        projectCount_other: '{{count}} matters',
        /** Left-pane heading. */
        stepClient: '1. Client',
        /** Right-pane heading. */
        stepMatter: '2. Matter',
        /** Accessible name of the matter radiogroup. */
        matterGroupAriaLabel: 'Matter',
        /** The radio that starts an invoice with no matter. */
        noMatterOption: 'No matter – direct charges only',
        /** Matter row subtitle when the matter has nothing unbilled (reached from the matter's own page). */
        matterNoUnbilledWork: 'No unbilled work',
        /** Under the actions once a choice is made; plural on {{count}}, the client's other ready matters. */
        otherMattersNote_one: '{{count}} other matter has unbilled work. Invoice it separately.',
        otherMattersNote_other: '{{count}} other matters have unbilled work. Invoice them separately.',
        /** Chosen matter's approved time entries that sit on another draft; links to each draft follow. */
        onOtherDraft_one: '{{count}} time entry is already on another draft, so it is not added.',
        onOtherDraft_other: '{{count}} time entries are already on another draft, so they are not added.',
        /** Chosen matter has no approved work to start the invoice with. */
        matterWorkEmpty: 'No approved work on this matter yet. The invoice starts empty.',
        /** Chosen matter has more entries than one create takes; {{limit}} start the draft. */
        matterWorkOverCap: 'This matter has {{total}} entries to bill. The invoice starts with the first {{limit}}; add the other {{remaining}} from the draft.',
        /** Reading the chosen matter's unbilled work. */
        matterWorkLoading: 'Checking unbilled work...',
        matterWorkError: "Couldn't load this matter's unbilled work.",
        matterWorkRetry: 'Try again',
        /** Fallback when creating the invoice fails without a usable message. */
        createError: "Couldn't create the invoice. Try again.",
        /** Link text for another draft that holds an entry, when that draft has no invoice number yet. */
        unnumberedDraft: 'Draft',
        /** Accessible label for the project-filter input. */
        filterProjectsAriaLabel: 'Filter matters',
        /** Placeholder for the project-filter input. */
        filterProjectsPlaceholder: 'Filter matters...',
        /** Empty state when the filter matches no projects. */
        noProjectsMatchFilter: 'No matters match your filter',
        /**
         * Work-composition counts on a client row and a matter row, so the
         * reader can see that an invoice would pick up costs as well as time.
         * i18next plural pairs, interpolating {{count}}. The activity count is
         * DRAFT+APPROVED, matching the unbilled amount shown beside it; the
         * disbursement count is the APPROVED unbilled set the editor auto-adds.
         *
         * The `WithDraft` variant is used whenever any of those activities are
         * still DRAFT, because only the APPROVED ones are auto-added to the
         * invoice on the next step - an unqualified total would promise work
         * the editor then silently leaves behind. Plural is on {{count}} (the
         * DRAFT+APPROVED total); {{draft}} is the DRAFT subset of it.
         */
        activityCount_one: '{{count}} activity',
        activityCount_other: '{{count}} activities',
        activityCountWithDraft_one: '{{count}} activity ({{draft}} draft)',
        activityCountWithDraft_other: '{{count}} activities ({{draft}} draft)',
        disbursementCount_one: '{{count}} disbursement',
        disbursementCount_other: '{{count}} disbursements',
        /**
         * Hint under the actions when the pending selection carries DRAFT
         * activities. The editor auto-adds only APPROVED work, so without this
         * the user continues expecting the row's headline count and lands on a
         * shorter invoice with nothing saying why. Plural on {{count}}, the
         * DRAFT activities across the selected matters.
         */
        draftActivitiesNotAutoAddedHint_one: "{{count}} draft activity won't be added automatically. Approve it first, or add it on the next step.",
        draftActivitiesNotAutoAddedHint_other: "{{count}} draft activities won't be added automatically. Approve them first, or add them on the next step.",
    },
    /** Dashboard WIP summary surfaces (`routes/dashboard.tsx`). */
    dashboard: {
        /** Label on the dashboard's unbilled-work figure, in the chat card's top row; kept short to fit beside Outstanding on a phone. */
        wipPillLabel: 'WIP',
    },
    /** How an invoice's status reads wherever invoices are listed, on screen and in the list exports (`describeInvoiceStatus`). */
    status: {
        draft: 'Draft',
        approved: 'Approved',
        sent: 'Sent',
        paid: 'Paid',
        voided: 'Voided',
        /** A draft carrying a live (sent, not cancelled) pro forma. */
        proFormaSent: 'Pro forma sent',
        /** Covered wholly by a bad-debt write-off, with no cash received. */
        writtenOff: 'Written off',
        /** Covered wholly by credit notes, with no cash received. */
        credited: 'Credited',
        partiallyPaid: 'Partially paid',
        /** Still owing after its due date (the invoice date when none is set). */
        overdue: 'Overdue',
        /** Overdue but still only approved: the client may never have received it. */
        overdueNotSent: 'Overdue – not sent',
        /** A soft-deleted draft, read from `archivedAt` (it keeps DRAFT underneath); also the list's filter chip for them. */
        deleted: 'Deleted',
    },
    /** Invoice list table (`routes/invoices.tsx` `InvoiceList`). */
    list: {
        /** Muted client cell on a draft that has no client yet (invoice list, dashboard). */
        noClient: 'None',
        /** Screen-reader prefix for the matter name shown under the client (invoice list and issued-invoice header). */
        matterLabel: 'Matter:',
        /** Accessible label for the reference column's sort button. */
        sortByProjectReferenceAriaLabel: 'Sort by matter reference',
    },
    /** "Export to Xero" feedback on the invoice detail page (`routes/invoices.tsx` `InvoiceEditor`). */
    xero: {
        /** Persistent header badge shown whenever the invoice carries a Xero export. */
        badge: 'In Xero',
        /** Success toast title after an export that returned a link. */
        exportedTitle: 'Exported to Xero',
        /** Info toast title when the export call found the invoice already in Xero. */
        alreadyExportedTitle: 'Already exported to Xero',
        /** Warning toast description when the export succeeded but returned no link yet. */
        linkUnavailableDescription: 'The link to this invoice in Xero is not available yet. Open Xero to view it.',
        /** Reason shown on the disabled "View in Xero" menu item when there is no link. */
        linkUnavailableReason: 'No link to this invoice in Xero is available yet.',
        /**
         * Error toast title when the export failed because the Xero connection
         * needs reauth. Names the state ("Xero reconnection required"), matching
         * the house form for an error title elsewhere in this app ("Subscription
         * required", "Usage limit reached", "Connection failed") - the description
         * explains the state and the action button (reconnectAction) carries the
         * verb, so the title does not need to repeat it.
         */
        reconnectTitle: 'Xero reconnection required',
        /** Error toast description accompanying reconnectTitle. */
        reconnectDescription: 'Xero has stopped accepting the connection. Reconnect it, then export again.',
        /** Label for the reconnect toast's action button, which navigates to the Xero settings tab. */
        reconnectAction: 'Reconnect Xero',
    },
};
